/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the image context toolbar
   (Modules R2 Phase 5, task 4: the width presets that used to live in
   the right-click menu move to an on-block control, same pattern as
   `TableToolbar` — a small bar that appears while a figure is selected,
   anchored above it, portalled + viewport-clamped (DESIGN §4).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/react';
import { selectedImage, setImageWidth } from './commands';
import { appLayerProps } from '../components/ui/app-layer';

interface Props {
  editor: Editor;
  canEdit: boolean;
}

const IMAGE_WIDTHS = [25, 50, 75, 100];
const TOOLBAR_H = 36;

export function ImageToolbar({ editor, canEdit }: Props) {
  // Same reasoning as TableToolbar: "are we on an image" is selection state,
  // "should we even be showing" is focus state.
  const [, force] = React.useReducer((x: number) => x + 1, 0);
  React.useEffect(() => {
    editor.on('transaction', force);
    editor.on('focus', force);
    editor.on('blur', force);
    return () => {
      editor.off('transaction', force);
      editor.off('focus', force);
      editor.off('blur', force);
    };
  }, [editor]);

  const keepFocus = (e: React.MouseEvent) => e.preventDefault();

  const image = editor.isFocused ? selectedImage(editor.state) : null;
  const [rect, setRect] = React.useState<DOMRect | null>(null);

  React.useLayoutEffect(() => {
    if (!image) {
      setRect(null);
      return;
    }
    const place = () => {
      const dom = editor.view.nodeDOM(image.pos) as HTMLElement | null;
      setRect(dom ? dom.getBoundingClientRect() : null);
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image?.pos, editor.state]);

  if (!image || !rect) return null;

  const run = (fn: () => void) => () => {
    fn();
    editor.commands.focus();
  };

  const width = 220;
  const top = Math.max(8, rect.top - TOOLBAR_H - 8);
  const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - width - 8));

  return createPortal(
    <div
      {...appLayerProps}
      className="dsg-table-tb"
      role="toolbar"
      aria-label="Image width"
      onMouseDown={keepFocus}
      style={{ top, left }}
    >
      <span className="dsg-tbl-pop-label" style={{ padding: '0 var(--space-1)' }}>
        Width
      </span>
      {IMAGE_WIDTHS.map((w) => (
        <button
          key={w}
          type="button"
          role="radio"
          aria-checked={image.width === w}
          className={`sps-width-btn${image.width === w ? ' is-on' : ''}`}
          onClick={run(() =>
            setImageWidth(editor.state, editor.view.dispatch.bind(editor.view), w)
          )}
          disabled={!canEdit}
        >
          {w}%
        </button>
      ))}
    </div>,
    document.body
  );
}
